import { faCheck, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Checkbox, Tooltip } from 'component-library';
import _ from 'lodash';
import interceptorImg from '../../../../assets/lcpIcons/interceptorsetup-01.png';
import systemSetupImg from '../../../../assets/lcpIcons/panelsettings.png';
import rudderImg from '../../../../assets/lcpIcons/ruddersignalnew-01.png';
import sensorImg from '../../../../assets/lcpIcons/sensorsetup-01.png';
import speedImg from '../../../../assets/lcpIcons/speedsource-01.png';
import interceptorImgWhite from '../../../../assets/lcpIcons/white/interceptorsetup-01.png';
import systemSetupImgWhite from '../../../../assets/lcpIcons/white/panelsettings.png';
import rudderImgWhite from '../../../../assets/lcpIcons/white/ruddersignalnew-01.png';
import sensorImgWhite from '../../../../assets/lcpIcons/white/sensorsetup-01.png';
import speedImgWhite from '../../../../assets/lcpIcons/white/speedsource-01.png';
import { useTheme } from '../../../../contexts/ThemeContext';
import { Preset } from '../../../../models/ApiModels';
import { ToggleButton } from '../../../../utils/ToggleButton';
interface Props {
  sensorData: { id: string; name: string; value: number; unit: string; hasTooltip: boolean; defaultValue: number }[];
  rudderData: { id: string; name: string; value: number; unit: string; defaultValue: number; displayValue?: string }[];
  interceptorSetupData: { id: string; name: string; value: number; unit: string; defaultValue: number }[];
  speedSourceData: { id: string; name: string; value: boolean; unit: string; defaultValue: boolean }[];
  selectBoxes?: boolean;
  preset?: Preset;
  setPreset?: (preset: Preset) => void;
  isPreset?: boolean;
  compareDefault: boolean;
  setCompareDefault: (compareDefault: boolean) => void;
}

export function ConfigurationSystemSetupSettings(props: Props) {
  const { resolvedTheme } = useTheme();
  function handleEnabledValues(clickedName: any, clickedValue: any, clickedId: string) {
    if (
      clickedName.includes('Theme') ||
      clickedName.includes('Orientation') ||
      clickedName.includes('Allow nofix') ||
      clickedName.includes('Prioritize waterspeed')
    ) {
      if (props.preset && props.setPreset) {
        const newPreset = { ...props.preset };
        if (Object.prototype.hasOwnProperty.call(newPreset.lcpMetadata, clickedId)) {
          //check if key exists
          delete newPreset.lcpMetadata[clickedId];
        } else {
          newPreset.lcpMetadata[clickedId] = clickedValue;
        }
        props.setPreset(newPreset);
      }
    } else {
      if (props.preset && props.setPreset) {
        const newPreset = { ...props.preset };
        if (Object.prototype.hasOwnProperty.call(newPreset.lcuMetadata, clickedId)) {
          //check if key exists
          delete newPreset.lcuMetadata[clickedId];
        } else {
          newPreset.lcuMetadata[clickedId] = clickedValue;
        }
        props.setPreset(newPreset);
      }
    }
  }

  function handleTooltipHeading(id: string) {
    if (id === 'axisConfig') return 'LCU Orientation';
    return '';
  }

  function handleToolDescription(id: string) {
    if (id === 'axisConfig')
      return 'This is the value of the orientation, displayed in the control panel together with an illustration';
    return '';
  }

  return (
    <div>
      <span className='flex items-center justify-between pb-5 mb-5 border-b border-gray-10'>
        <h4 className='prose-heading4 '>
          <img
            src={resolvedTheme === 'light' ? systemSetupImg : systemSetupImgWhite}
            className='inline mr-1 object-contain'
            alt='system setup'
          />
          System setup settings
        </h4>
        <div className='flex items-center gap-2'>
          <Tooltip
            heading='Highlight changes'
            description='Every setting that is changed from default will be highlighted in blue. Every setting that is still default will be grayed out.'
          />
          <ToggleButton
            toggled={props.compareDefault}
            onClick={() => props.setCompareDefault && props.setCompareDefault(!props.compareDefault)}
            label='Highlight changes'
            className=''
          />
        </div>
      </span>
      <div className='grid grid-cols-[repeat(auto-fit,minmax(16rem,auto))] gap-10 border-b border-gray-10 pb-5'>
        {/* Sensor */}
        <div className='flex flex-col gap-4'>
          <h5 className='prose-heading5'>
            <img
              src={resolvedTheme === 'light' ? sensorImg : sensorImgWhite}
              className='inline mr-1 object-contain'
              alt='sensor'
            />
            Sensor
          </h5>
          {props.sensorData.map(({ name, value, id, unit, hasTooltip, defaultValue }) => (
            <div
              key={id}
              className={`${props.compareDefault && !_.isEqual(value, defaultValue) && 'text-primary-100 font-bold'} ${
                props.compareDefault && _.isEqual(value, defaultValue) && 'text-gray-60'
              } flex gap-2`}
            >
              {props.selectBoxes && props.preset?.lcuMetadata && (
                <Checkbox
                  disabled={value === null || value === undefined}
                  active={
                    Object.keys(props.preset?.lcpMetadata).includes(id) ||
                    Object.keys(props.preset?.lcuMetadata).includes(id)
                  }
                  onClick={() => handleEnabledValues(name, value, id)}
                />
              )}
              <p
                className={`${
                  (value === null && props.isPreset) || (value === undefined && props.isPreset) ? 'text-gray-40' : ''
                }`}
              >
                {name}
              </p>
              {hasTooltip && <Tooltip heading={handleTooltipHeading(id)} description={handleToolDescription(id)} />}
              {value === null || value === undefined ? (
                <p className='text-gray-40 italic ml-auto'>{props.isPreset ? '-' : 'n/a'}</p>
              ) : (
                <p className='font-code ml-auto'>{Math.abs(value) + unit}</p>
              )}
            </div>
          ))}
        </div>
        {/* Rudder signal */}
        <div className='flex flex-col gap-4'>
          <h5 className='prose-heading5'>
            <img
              src={resolvedTheme === 'light' ? rudderImg : rudderImgWhite}
              className='inline mr-1 object-contain'
              alt='rudder signal'
            />
            Rudder signal
          </h5>
          {props.rudderData.map(({ name, value, id, unit, defaultValue, displayValue }) => (
            <div
              key={id}
              className={`${props.compareDefault && !_.isEqual(value, defaultValue) && 'text-primary-100 font-bold'} ${
                props.compareDefault && _.isEqual(value, defaultValue) && 'text-gray-60'
              } flex gap-2`}
            >
              {props.selectBoxes && props.preset?.lcuMetadata && (
                <Checkbox
                  disabled={value === null || value === undefined}
                  active={
                    Object.keys(props.preset?.lcpMetadata).includes(id) ||
                    Object.keys(props.preset?.lcuMetadata).includes(id)
                  }
                  onClick={() => handleEnabledValues(name, value, id)}
                />
              )}
              <p
                className={`${
                  (value === null && props.isPreset) || (value === undefined && props.isPreset) ? 'text-gray-40' : ''
                }`}
              >
                {name}
              </p>
              {value === null || value === undefined ? (
                <p className='text-gray-40 italic ml-auto'>{props.isPreset ? '-' : 'n/a'}</p>
              ) : (
                <p className='font-code ml-auto'>{displayValue || value + unit}</p>
              )}
            </div>
          ))}
        </div>
        {/* Interceptor setup */}
        <div className='flex flex-col gap-4'>
          <h5 className='prose-heading5'>
            <img
              src={resolvedTheme === 'light' ? interceptorImg : interceptorImgWhite}
              className='inline mr-1 object-contain'
              alt='interceptor setup'
            />
            Interceptor setup
          </h5>
          {props.interceptorSetupData.map(({ name, value, id, unit, defaultValue }) => (
            <div
              key={id}
              className={`${props.compareDefault && !_.isEqual(value, defaultValue) && 'text-primary-100 font-bold'} ${
                props.compareDefault && _.isEqual(value, defaultValue) && 'text-gray-60'
              } flex gap-2`}
            >
              {props.selectBoxes && props.preset?.lcuMetadata && (
                <Checkbox
                  disabled={value === null || value === undefined}
                  active={
                    Object.keys(props.preset?.lcpMetadata).includes(id) ||
                    Object.keys(props.preset?.lcuMetadata).includes(id)
                  }
                  onClick={() => handleEnabledValues(name, value, id)}
                />
              )}
              <p
                className={`${
                  (value === null && props.isPreset) || (value === undefined && props.isPreset) ? 'text-gray-40' : ''
                }`}
              >
                {name}
              </p>
              {value === null || value === undefined ? (
                <p className='text-gray-40 italic ml-auto'>{props.isPreset ? '-' : 'n/a'}</p>
              ) : (
                <p className='font-code ml-auto'>{value + unit}</p>
              )}
            </div>
          ))}
        </div>
        {/* Speed source */}
        <div className='flex flex-col gap-4'>
          <h5 className='prose-heading5'>
            <img
              src={resolvedTheme === 'light' ? speedImg : speedImgWhite}
              className='inline mr-1 object-contain'
              alt='speed source'
            />
            Speed source
          </h5>
          {props.speedSourceData.map(({ name, value, id, unit, defaultValue }) => (
            <div
              key={id}
              className={`${props.compareDefault && !_.isEqual(value, defaultValue) && 'text-primary-100 font-bold'} ${
                props.compareDefault && _.isEqual(value, defaultValue) && 'text-gray-60'
              } flex gap-2`}
            >
              {props.selectBoxes && props.preset?.lcuMetadata && (
                <Checkbox
                  disabled={value === null || value === undefined}
                  active={
                    Object.keys(props.preset?.lcpMetadata).includes(id) ||
                    Object.keys(props.preset?.lcuMetadata).includes(id)
                  }
                  onClick={() => handleEnabledValues(name, value, id)}
                />
              )}
              <p
                className={`${
                  (value === null && props.isPreset) || (value === undefined && props.isPreset) ? 'text-gray-40' : ''
                }`}
              >
                {name}
              </p>
              {value === null || value === undefined ? (
                <p className='text-gray-40 italic ml-auto'>{props.isPreset ? '-' : 'n/a'}</p>
              ) : (
                <p className='font-code ml-auto'>
                  {value ? <FontAwesomeIcon icon={faCheck} /> : <FontAwesomeIcon icon={faXmark} />}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
